import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { ArrowRight, FileSearch, Monitor, Bot, Headphones } from 'lucide-react';

const solutions = [
  {
    title: 'AUTOMATION SYSTEMS',
    desc: 'Complete PLC and robotic control systems for manufacturing lines. From concept to commissioning.',
    image: '/solution-automation.webp',
  },
  {
    title: 'INTEGRATION ARCHITECTURE',
    desc: 'Multi-vendor system design connecting disparate equipment into unified operational control.',
    image: '/solution-integration.webp',
  },
  {
    title: 'TECHNICAL DEPLOYMENT',
    desc: 'Rapid specialist deployment for urgent commissioning, fault finding, and maintenance requirements.',
    image: '/solution-deployment.webp',
  },
];

const steps = [
  { num: '01', title: 'ASSESS', desc: 'Technical audit and requirement specification.', icon: FileSearch },
  { num: '02', title: 'DESIGN', desc: 'System architecture and integration planning.', icon: Monitor },
  { num: '03', title: 'DEPLOY', desc: 'Installation, programming, and commissioning.', icon: Bot },
  { num: '04', title: 'SUPPORT', desc: 'Ongoing maintenance and after-sales technical support.', icon: Headphones },
];

export default function Solutions() {
  useDocumentTitle('Solutions — Straton Group');

  return (
    <main id="main-content">
      {/* ── Hero Section ── */}
      <section className="relative min-h-[60vh] lg:min-h-[70vh] flex items-end">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="/solutions-hero.webp" fetchPriority="high"
            alt="Industrial automation facility"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-bg-primary via-bg-primary/80 to-bg-primary/40" />
        </div>

        {/* Text content */}
        <div className="relative z-10 w-full max-w-[1400px] mx-auto section-padding pt-24 lg:pt-32 pb-16 lg:pb-24">
          {/* Label with line */}
          <div className="flex items-center gap-4 mb-6">
            <div className="w-8 h-px bg-text-muted" />
            <span className="text-xs uppercase tracking-[0.2em] text-text-muted">
              Solutions
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold uppercase tracking-[0.15em] text-text-primary mb-6 leading-tight">
            Engineered Systems<br />
            For Complex Industrial<br />
            Environments.
          </h1>

          <div className="w-8 h-px bg-text-muted" />
        </div>
      </section>

      {/* ── Solution Cards ── */}
      <section className="bg-bg-primary section-padding py-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="border border-border-secondary grid grid-cols-1 lg:grid-cols-3">
            {solutions.map((solution, i) => (
              <div
                key={solution.title}
                className={`relative min-h-[420px] lg:min-h-[520px] overflow-hidden ${
                  i < 2 ? 'border-b lg:border-b-0 lg:border-r border-border-secondary' : ''
                }`}
              >
                {/* Background image */}
                <img
                  src={solution.image}
                  alt={solution.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-bg-primary/95 via-bg-primary/75 to-bg-primary/50" />

                {/* Content */}
                <div className="relative z-10 p-8 lg:p-10 flex flex-col h-full">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-text-muted mb-3">
                    SOLUTION {String(i + 1).padStart(2, '0')}
                  </span>

                  <div className="w-6 h-px bg-text-muted mb-6" />

                  <h3 className="text-lg lg:text-xl font-semibold uppercase tracking-[0.1em] text-text-primary mb-4">
                    {solution.title}
                  </h3>

                  <p className="text-sm text-text-secondary leading-relaxed mb-auto max-w-[280px]">
                    {solution.desc}
                  </p>

                  <div className="mt-10">
                    <span className="inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.15em] text-text-muted">
                      EXPLORE <ArrowRight className="w-3 h-3" />
                    </span>
                    <div className="w-6 h-px bg-text-muted mt-3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Deployment Process ── */}
      <section className="bg-bg-primary section-padding pb-20">
        <div className="max-w-[1400px] mx-auto">
          {/* Label with line */}
          <div className="flex items-center gap-4 mb-6">
            <div className="w-8 h-px bg-text-muted" />
            <span className="text-xs uppercase tracking-[0.2em] text-text-muted">
              How We Work
            </span>
          </div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold uppercase tracking-[0.15em] text-text-primary mb-10">
            Deployment Process
          </h2>

          <div className="border border-border-secondary">
            <div className="grid grid-cols-2 lg:grid-cols-4">
              {steps.map((step, i) => (
                <div
                  key={step.title}
                  className={`flex flex-col items-center text-center py-12 lg:py-16 px-6 ${
                    i < steps.length - 1 ? 'border-r border-border-secondary' : ''
                  } ${i === 1 ? 'border-r-0 lg:border-r' : ''}`}
                >
                  {/* Icon */}
                  <step.icon
                    className="w-6 h-6 text-text-muted mb-6"
                    strokeWidth={1.2}
                  />

                  {/* Number */}
                  <span className="text-sm text-text-muted mb-2">
                    {step.num}
                  </span>

                  {/* Title */}
                  <span className="text-xs uppercase tracking-[0.15em] text-text-primary mb-3">
                    {step.title}
                  </span>

                  {/* Divider */}
                  <div className="w-6 h-px bg-text-muted mb-4" />

                  {/* Description */}
                  <span className="text-[10px] text-text-muted leading-relaxed max-w-[160px]">
                    {step.desc}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
